import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getEloTier } from '../../../utils/eloUtils';
import clsx from 'clsx';

interface EloChangePopupProps {
  oldElo: number;
  newElo: number;
  onClose: () => void;
}

const EloChangePopup: React.FC<EloChangePopupProps> = ({
  oldElo,
  newElo,
  onClose
}) => {
  const [currentElo, setCurrentElo] = useState(oldElo);
  const [timeLeft, setTimeLeft] = useState(5);
  const [isPaused, setIsPaused] = useState(false);
  const eloDiff = newElo - oldElo;
  const isPositive = eloDiff > 0;

  const oldTier = getEloTier(oldElo);
  const newTier = getEloTier(newElo);
  const isTierChange = oldTier.name !== newTier.name;

  // Auto-close timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (!isPaused && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [isPaused, timeLeft, onClose]);

  // ELO counter animation
  useEffect(() => {
    let startTime = Date.now();
    const duration = 1500;
    const startValue = oldElo;
    const endValue = newElo;
    const difference = endValue - startValue;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.round(startValue + difference * easeOutQuart);

      setCurrentElo(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [oldElo, newElo]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.5, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.5, y: 20 }}
        className="relative w-full max-w-md"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="btn btn-circle btn-sm absolute -top-2 -right-2 z-10 bg-base-300"
        >
          <svg 
            className="w-4 h-4"
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
            strokeWidth="2"
          >
            <path 
              strokeLinecap="round" 
              d="M6 6L18 18M6 18L18 6"
            />
          </svg>
        </button>

        <div className="card bg-base-300 shadow-xl">
          {/* Timer bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-base-content/10">
            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 5, ease: 'linear' }}
              className={clsx(
                'h-full',
                isPositive ? 'bg-success' : 'bg-error'
              )}
            />
          </div>

          <div className="card-body">
            <div className="flex items-center gap-6">
              <div className="relative w-16 h-16">
                {/* Old Tier Icon */}
                <motion.div
                  initial={{ opacity: 1, scale: 1 }}
                  animate={isTierChange ? { opacity: 0, scale: 0.5 } : {}}
                  className="absolute inset-0"
                >
                  <div className="w-full h-full rounded-full flex items-center justify-center bg-base-200">
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={oldTier.color}>
                      <path d={oldTier.icon} />
                    </svg>
                  </div>
                </motion.div>

                {/* New Tier Icon */}
                {isTierChange && (
                  <motion.div
                    initial={{ opacity: 0, scale: 1.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0"
                  >
                    <div className="w-full h-full rounded-full flex items-center justify-center bg-base-200">
                      <svg className="w-10 h-10" viewBox="0 0 24 24" fill={newTier.color}>
                        <path d={newTier.icon} />
                      </svg>
                    </div>
                  </motion.div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="card-title text-2xl">
                  {isTierChange ? 'Novo Rank!' : 'ELO Rating'}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">
                    {currentElo}
                  </span>
                  <span className={clsx(
                    'text-xl font-medium',
                    isPositive ? 'text-success' : 'text-error'
                  )}>
                    {isPositive ? '+' : ''}{eloDiff}
                  </span>
                </div>
                {isTierChange && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-lg text-base-content/70"
                  >
                    {oldTier.name} → {newTier.name}
                  </motion.div>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <progress 
                className={clsx(
                  'progress w-full',
                  isPositive ? 'progress-success' : 'progress-error'
                )}
                value={100} 
                max={100}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EloChangePopup;
